import ApplicationController from '@app/javascript/controllers/application_controller'
import Highcharts from 'highcharts'
import I18n from '@app/javascript/i18n/index.js.erb'
import { useDispatch } from 'stimulus-use'
import { startCase } from 'lodash'

Highcharts.maps['custom/world-highres'] = require('@highcharts/map-collection/custom/world-highres.geo.json')
Highcharts.maps['countries/us/custom/us-small'] = require('@highcharts/map-collection/countries/us/custom/us-small.geo.json')
Highcharts.maps['countries/ca/ca-all'] = require('@highcharts/map-collection/countries/ca/ca-all.geo.json')

export default class extends ApplicationController {
  connect() {
    useDispatch(this)
    Highcharts.setOptions({
      lang: { thousandsSep: ',' }
    })
    addEventListener('filters-count-updater:afterRetrieveCounts', this.createCharts.bind(this))
  }

  createCharts(event) {
    const counts = event.detail.counts
    const searchParams = event.detail.searchParams
    this.visibleCharts = []
    this.createLegislationCharts(counts, searchParams)
    this.createMaps(counts, searchParams)
    if (this.visibleCharts.length > 0) {
      this.element.classList.remove('hidden')
    } else {
      this.element.classList.add('hidden')
    }
  }

  createMaps(counts, searchParams) {
    const filteredLocations = searchParams.getAll('locations[]')
    const locationCounts = Object.entries(counts.locations).filter(([location, _]) => {
      return filteredLocations.length === 0 || (filteredLocations.includes(location) || filteredLocations.includes(`PS-${location}`))
    })
    const contentType = this.getContentType(searchParams)
    const countryCounts = locationCounts.filter(([location, _]) => !this.isCountry(location))
    this.createMap('custom/world-highres', '#policy_countries_map_chart', `Country ${contentType} Counts`, countryCounts)
    const usStateCounts = locationCounts.filter(([location, _]) => this.isState(location, 'US'))
    this.createMap('countries/us/custom/us-small', '#policy_us_map_chart', `US ${contentType} Counts`, usStateCounts)
    const caStateCounts = locationCounts.filter(([location, _]) => this.isState(location, 'CA'))
    this.createMap('countries/ca/ca-all', '#policy_ca_map_chart', `CA ${contentType} Counts`, caStateCounts)
  }

  createLegislationCharts(counts, searchParams) {
    const contentTypes = searchParams.getAll('content_type[]')
    const legislationSelected = contentTypes.includes('policy.legislation')
    const showPolicyStatus = legislationSelected && searchParams.getAll('policy_legislation_status[]').length === 0
    this.createPieChart('#policy_status_chart', 'Legislation Status',
      this.policyStatusData(counts), 'legislationStatusSelected', showPolicyStatus)
    const showPolicyType = legislationSelected && searchParams.getAll('policy_leg_type[]').length === 0
    this.createPieChart('#policy_type_chart', 'Legislation Type',
      this.policyTypeData(counts), 'legislationTypeSelected', showPolicyType)
  }

  getContentType(searchParams) {
    let contentType = 'Policy'
    const subContentTypes = searchParams.getAll('content_type[]')
    if (subContentTypes.length === 1) {
      contentType = startCase(subContentTypes[0].split('.').pop())
    }
    return contentType
  }

  createMap(map, selector, title, counts) {
    const self = this
    const container = document.querySelector(selector)
    if (!this.updateChartVisibility(container, counts.length > 1)) {
      return
    }

    Highcharts.mapChart(container, {
      chart: {
        map: map
      },
      title: {
        text: title
      },
      credits: this.credits(),
      colorAxis: {
        type: 'logarithmic',
        stops: [[0.2, '#ece9ed'], [0.4, '#CFB1ED'], [0.6, '#A57ECA'], [0.8, '#754C87'], [1, '#442547']],
        minColor: '#FFFFFF',
        maxColor: '#442547',
        labels: {
          style: {
            textOverflow: 'none'
          },
          formatter: function(params) {
            return Intl.NumberFormat('en-GB', { notation: 'compact' }).format(params.value)
          }
        }
      },
      exporting: this.exportingOptions(),
      legend: {
        enabled: true,
        layout: 'horizontal'
      },
      plotOptions: {
        map: {
          allAreas: false
        },
        series: {
          cursor: 'pointer',
          point: {
            events: {
              click: function() {
                return self.dispatch('mapLocationSelected', { locationCode: this['hc-key'] })
              }
            }
          }
        }
      },
      series: this.mapSeries(counts)
    })
  }

  mapSeries(counts) {
    const data = counts.map(([location, count]) => {
      return {
        'hc-key': location.toLowerCase(),
        value: count
      }
    })

    return [
      {
        dataLabels: {
          formatter: function(params) {
            return this.point.properties ? this.point.properties['hc-a2'] : null
          },
          enabled: true
        },
        name: 'Count',
        data: data
      }
    ]
  }

  createPieChart(selector, title, data, clickMethod, visible = true) {
    const self = this
    const maxSlices = 10
    const container = document.querySelector(selector)
    if (!this.updateChartVisibility(container, visible && data.length > 0)) {
      return
    }
    data = data.sort((a, b) => { return b.y - a.y })
    const otherTotal = data.slice(maxSlices - 1).reduce((total, val) => total + val.y, 0)
    data = data.slice(0, maxSlices - 1)
    if (otherTotal > 0) {
      data = data.concat({ name: 'Other', y: otherTotal })
    }

    Highcharts.chart(container, {
      chart: {
        type: 'pie'
      },
      title: {
        text: title
      },
      credits: this.credits(),
      exporting: this.exportingOptions(),
      tooltip: {
        pointFormat: '{series.name}: <b>{point.y} ({point.percentage:.1f}%)</b>'
      },
      accessibility: {
        point: {
          valueSuffix: '%'
        }
      },
      plotOptions: {
        pie: {
          colors: ['#754C87', '#CFB1ED', '#087d85', '#9ccbce', '#e96f54', '#f3ad9d', '#3ac6cd', '#9de3e6', '#f0c11c', '#f8e08e'],
          dataLabels: {
            enabled: true,
            format: '<b>{point.name}</b>: {point.percentage:.1f} %'
          },
          innerSize: '30%'
        },
        series: {
          cursor: 'pointer',
          point: {
            events: {
              click: function() {
                return self.dispatch(clickMethod, { key: this['hc-key'] })
              }
            }
          }
        }
      },
      series: [{
        name: title,
        colorByPoint: true,
        data: data
      }]
    })
  }

  policyStatusData(counts) {
    return Object.entries(counts.policy_legislation_status).map(([status, count]) => {
      return {
        name: this.humanizedName('entities.show.overview.field.us_bill.status', status),
        y: count,
        'hc-key': status
      }
    })
  }

  policyTypeData(counts) {
    return Object.entries(counts.policy_leg_type).map(([type, count]) => {
      return {
        name: this.humanizedName('search_v2.filters.policy_leg_type.values', type),
        y: count,
        'hc-key': type
      }
    })
  }

  humanizedName(translatePath, key) {
    return I18n.t(`${translatePath}.${key}`, { defaultValue: startCase(key.toLowerCase()) })
  }

  updateChartVisibility(container, isVisible) {
    container.style.display = isVisible ? 'block' : 'none'
    if (isVisible) {
      this.visibleCharts.push(container)
    }
    return isVisible
  }

  credits() {
    return {
      text: 'Credit: FiscalNote',
      useHTML: true,
      mapText: '',
      mapTextFull: '',
      href: ''
    }
  }

  exportingOptions() {
    return {
      buttons: {
        contextButton: {
          // Returns menu items without the viewFullscreen button since that doesn't work in an iframe.
          menuItems: ['printChart', 'separator', 'downloadPNG', 'downloadJPEG', 'downloadPDF', 'downloadSVG']
        }
      }
    }
  }

  isCountry(code) {
    return code.includes('-')
  }

  isState(code, countryCode) {
    return code.startsWith(`${countryCode}-`)
  }
}
